import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { QuoteItemChangeReview as Review } from './quoteReviewContract';

const CHANGED_SX = { bgcolor: 'warning.light', color: 'warning.contrastText' } as const;

/**
 * The server-built review of one quote line change (#2546): the line before
 * and after, the COMPLETE resulting price lines and the totals before → after.
 * Every changed value is highlighted; nothing is computed here.
 */
export function QuoteItemChangeReview({ review }: { review: Review }) {
  const { t, i18n } = useTranslation('assistant');
  const { t: tc } = useTranslation('common');
  const { t: tw } = useTranslation('workorders');
  const q = review.quote;
  const result = review.result.pricing;
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';
  const number = (value: number) =>
    new Intl.NumberFormat(locale, { maximumFractionDigits: 8 }).format(value);
  // Display only: net unit prices keep four decimals in cents (six in euros).
  const money = (cents: number, unit = false) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: q.pricing.currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: unit ? 6 : 2,
    }).format(cents / 100);
  const unitLabel = (unit: string) => tc(`units.${unit}`, { defaultValue: unit });
  const removed = review.command.action === 'remove';
  const changed = (field: 'laborCostCents' | 'partsCostCents') =>
    review.changedFields.includes(field);

  const amountRow = (field: 'laborCostCents' | 'partsCostCents', label: string) => (
    <Typography
      variant="body2"
      data-testid={`quote-item-change-${field}`}
      data-changed={changed(field) ? 'true' : 'false'}
      sx={changed(field) ? { ...CHANGED_SX, px: 0.5, borderRadius: 0.5 } : undefined}
    >
      <strong>{label}: </strong>
      {money(review.before[field])}
      {review.after && changed(field) ? ` → ${money(review.after[field])}` : ''}{' '}
      {t('quoteReview.netLabel')}
    </Typography>
  );

  const totalRow = (key: 'net' | 'tax' | 'gross', before: number, after: number) => (
    <Typography
      variant="body2"
      data-testid={`quote-item-change-total-${key}`}
      data-changed={before !== after ? 'true' : 'false'}
      sx={before !== after ? { ...CHANGED_SX, px: 0.5, borderRadius: 0.5 } : undefined}
    >
      <strong>{t(`quoteReview.${key}`)}: </strong>
      {before === after ? money(after) : `${money(before)} → ${money(after)}`}
    </Typography>
  );

  return (
    <Box data-testid="quote-item-change-review" sx={{ mb: 2 }}>
      <Typography variant="subtitle2">
        {q.workOrderNumber} · {q.vehicle.registrationNumber}
      </Typography>
      <Typography variant="body2">
        <strong>{t('quoteReview.customer')}: </strong>
        {q.orderer?.displayName ?? t('quoteReview.none')}
      </Typography>

      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        {removed ? t('quoteItemChange.removedLine') : t('quoteItemChange.changedLine')}
      </Typography>
      <Box
        data-testid="quote-item-change-line"
        sx={{
          pl: 1,
          borderLeft: '3px solid',
          borderColor: 'warning.main',
          ...(removed ? { textDecoration: 'line-through' } : {}),
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {review.before.description}
        </Typography>
        {amountRow('laborCostCents', t('quoteReview.labor'))}
        {amountRow('partsCostCents', t('quoteReview.parts'))}
      </Box>

      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        {t('quoteItemChange.resultingLines')}
      </Typography>
      {result.lines.length === 0 ? (
        <Typography variant="body2">{t('quoteItemChange.noLines')}</Typography>
      ) : (
        <TableContainer sx={{ my: 1 }}>
          <Table size="small" aria-label={t('quoteItemChange.resultingLines')}>
            <TableHead>
              <TableRow>
                {['description', 'quantity', 'unitNet', 'discount', 'vat', 'lineNet'].map((key) => (
                  <TableCell key={key}>{t(`quoteReview.${key}`)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {result.lines.map((line, index) => {
                const isChanged = line.workOrderItemId === review.itemId;
                return (
                  <TableRow
                    key={`${line.sortOrder}-${index}`}
                    data-changed={isChanged ? 'true' : 'false'}
                    sx={isChanged ? CHANGED_SX : undefined}
                  >
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                      {line.lineType === 'surcharge'
                        ? tw('detail.smallSuppliesSurcharge')
                        : line.description}
                      {isChanged && (
                        <Chip
                          size="small"
                          color="warning"
                          label={t('quoteItemChange.changed')}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {number(line.quantity)} {unitLabel(line.unit || 'piece')}
                    </TableCell>
                    <TableCell>{money(line.unitPriceCents, true)}</TableCell>
                    <TableCell>{number(line.discountPercent)}%</TableCell>
                    <TableCell>
                      {line.taxable
                        ? `${number(line.taxPercent ?? result.defaultVatPercent)}%`
                        : t('quoteReview.nonTaxable')}
                    </TableCell>
                    <TableCell>{money(line.lineTotalCents)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {totalRow('net', q.pricing.netSubtotalCents, result.netSubtotalCents)}
      {totalRow('tax', q.pricing.taxCents, result.taxCents)}
      {totalRow('gross', q.pricing.grossTotalCents, result.grossTotalCents)}
      <Typography variant="body2" sx={{ mt: 1 }}>
        {t('quoteItemChange.explanation')}
      </Typography>
      <Typography variant="body2">{t('quoteReview.noDelivery')}</Typography>
    </Box>
  );
}
